import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { pdfApi } from 'api';
import { useModal } from 'shared/hooks';
import { TaskKeysType } from 'shared/types';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import pdfRedSvg from '../../../assets/img/file-pdf-red.svg';
import pdfSvg from '../../../assets/img/file-pdf.svg';

interface Props {
  currentICLId: string;
  id: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  pdfUrl?: string;
  remoteTaskId?: string;
  pdfFile: { file: File; url: string } | null;
  setPdfFile: (file: { file: File; url: string } | null) => void;
  hasText?: boolean;
  closeModal?: () => void;
}

export const PdfInput = ({
  currentICLId,
  id,
  handleChangeTaskByKey,
  pdfUrl,
  remoteTaskId,
  pdfFile,
  setPdfFile,
  hasText,
  closeModal,
}: Props) => {
  const { t } = useTranslation();
  const pdfInputRef = createRef<HTMLInputElement>();
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const { isOpen: isRemovingPdfModalOpen, close: closeRemovingPdfModal, open: openRemovingPdfModal } = useModal();

  const handleAddPdfLocal = async () => {
    const pdfInput = pdfInputRef.current;

    if (!pdfInput || !pdfInput.files) {
      return;
    }

    const file = pdfInput.files[0];
    setPdfFile({ file, url: URL.createObjectURL(file) });
  };

  const sendPdf = async () => {
    const pdfInput = pdfInputRef.current;

    if (!pdfInput || !pdfInput.files) {
      return;
    }

    setIsPdfLoading(true);

    const file = pdfInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenPdfUrl = await pdfApi.addPdf(file, currentICLId, id);
      handleChangeTaskByKey(id, 'pdfUrl', gottenPdfUrl);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsPdfLoading(false);
  };

  const removePdf = async () => {
    setIsPdfLoading(true);

    try {
      remoteTaskId && (await pdfApi.removePdf(pdfUrl ?? (pdfFile?.url as string), currentICLId, id));
      handleChangeTaskByKey(id, 'pdfUrl', '');
      setPdfFile(null);
      toast.success(t('Successfully removed!'));
      closeRemovingPdfModal();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsPdfLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={pdfUrl || pdfFile ? t('remove pdf') : t('add pdf')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (pdfUrl || pdfFile) {
              openRemovingPdfModal();
            } else {
              pdfInputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {pdfUrl || pdfFile ? <img src={pdfRedSvg} alt="camera" /> : <img src={pdfSvg} alt="camera" />}

          <input
            id="pdfInput"
            type="file"
            className="hidden"
            accept="application/pdf"
            ref={pdfInputRef}
            onChange={() => (remoteTaskId ? sendPdf() : handleAddPdfLocal())}
            disabled={isPdfLoading || !!pdfUrl}
            title={t('add pdf')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">
              {pdfUrl || pdfFile ? t('remove pdf') : t('add pdf')}
            </p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isRemovingPdfModalOpen}
        close={closeRemovingPdfModal}
        title={t('remove pdf')}
        onHandle={removePdf}
        isSubmitButtonDisabled={isPdfLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
